import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Heading,
  HStack,
  Tbody,
  Text,
  VStack,
} from "@biblioteksentralen/react";
import { getNorwegianWeekday, getPeriodAsDates } from "@libry-content/common";
import { capitalize, last } from "radash";
import { getSelfServiceLabel } from "../../../utils/getSelfServiceLabel";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { useCommonData } from "../../layout/CommonDataProvider";
import { DateHelper } from "@libry-content/common";
import { OpeningHoursHelper, SpecialHoursList } from "../openingHoursHelper/openingHoursHelper";
import { StyledTable } from "../OpeningHoursTable";
import SpecialOpeningHoursDay from "./SpecialOpeningHoursDay";
import SpecialOpeningHoursPeriod, { SpecialOpeningHoursPeriodHeader } from "./SpecialOpeningHoursPeriod";

const DATE_FORMAT = "d. LLLL";

const SpecialOpeningHoursMonth = ({ specialOpeningHours }: { specialOpeningHours: SpecialHoursList }) => {
  const { t, lang } = useTranslation();
  const { site } = useCommonData();
  const normalHoursLabel = t("Betjent");
  const hasSelfService = !!site?.hasSelfService;
  const selfServiceLabel = capitalize(getSelfServiceLabel(lang, site));

  const openingHoursDataForMonth = specialOpeningHours.map((specialHours) => {
    const dates = getPeriodAsDates(specialHours);

    const normalHoursWithinPeriod = specialHours.normalHours
      ? dates.map(getNorwegianWeekday).map((weekday) => specialHours.normalHours![weekday]!)
      : [];

    const selfServiceHoursWithinPeriod =
      hasSelfService && !!specialHours.selfServiceHours
        ? dates.map(getNorwegianWeekday).map((weekday) => specialHours.selfServiceHours![weekday]!)
        : [];

    const normalHoursWithinPeriodAreEqual = OpeningHoursHelper.normalHoursEqual(normalHoursWithinPeriod);
    const selfServiceHoursWithinPeriodAreEqual = OpeningHoursHelper.selfServiceHoursEqual(selfServiceHoursWithinPeriod);
    const hoursWithinPeriodAreEqual = normalHoursWithinPeriodAreEqual && selfServiceHoursWithinPeriodAreEqual;

    const label =
      dates.length === 1 ? (
        <Text as="span" whiteSpace="nowrap">
          {capitalize(DateHelper.getLabel(lang, dates[0], DATE_FORMAT))}
        </Text>
      ) : (
        <>
          <Text as="span" whiteSpace="nowrap">
            {capitalize(DateHelper.getLabel(lang, dates[0], DATE_FORMAT))}
          </Text>{" "}
          <Text as="span">{t("til")}</Text>{" "}
          <Text as="span" whiteSpace="nowrap">
            {DateHelper.getLabel(lang, last(dates) ?? undefined, DATE_FORMAT)}
          </Text>
        </>
      );

    return {
      specialHours,
      dates,
      normalHoursWithinPeriod,
      selfServiceHoursWithinPeriod,
      hoursWithinPeriodAreEqual,
      label,
    };
  });

  // If the month contains a special opening hours period of more than seven days with differing
  // hours between days, the periods are displayed as separate accordions each containing a table.
  // Otherwise the whole month is displayed in a single table.
  const monthHasAccordion = openingHoursDataForMonth.find(
    ({ hoursWithinPeriodAreEqual, normalHoursWithinPeriod }) =>
      !hoursWithinPeriodAreEqual && normalHoursWithinPeriod.length > 7
  );

  if (monthHasAccordion) {
    return (
      <Accordion width="100%" reduceMotion allowMultiple>
        <VStack as="ol" listStyleType="none" align="start">
          {openingHoursDataForMonth.map(({ specialHours, label }) => (
            <AccordionItem key={specialHours._key} as="li" marginTop="0 !important" width="100%" border="none">
              <Heading as="h4">
                <AccordionButton boxShadow="none !important" padding="0.5rem 0.75rem">
                  <HStack width="100%" justify="space-between">
                    <Text>{label}</Text>
                    <HStack>
                      <Text>{t("Se tider")}</Text>
                      <AccordionIcon marginInlineStart="0 !important" />
                    </HStack>
                  </HStack>
                </AccordionButton>
              </Heading>
              <AccordionPanel padding="0">
                <SpecialOpeningHoursPeriod
                  specialHours={specialHours}
                  hasSelfService={hasSelfService}
                  normalHoursLabel={normalHoursLabel}
                  selfServiceLabel={selfServiceLabel}
                />
              </AccordionPanel>
            </AccordionItem>
          ))}
        </VStack>
      </Accordion>
    );
  }

  let tableItemIndex = -1;

  return (
    <StyledTable>
      <SpecialOpeningHoursPeriodHeader
        hasSelfService={hasSelfService}
        normalHoursLabel={normalHoursLabel}
        selfServiceLabel={selfServiceLabel}
      />
      <Tbody>
        {openingHoursDataForMonth.map(
          ({
            specialHours,
            dates,
            normalHoursWithinPeriod,
            selfServiceHoursWithinPeriod,
            hoursWithinPeriodAreEqual,
            label,
          }) => {
            if (hoursWithinPeriodAreEqual) {
              tableItemIndex += 1;

              return (
                <SpecialOpeningHoursDay
                  key={specialHours._key}
                  label={label}
                  normalHoursDay={normalHoursWithinPeriod[0]}
                  selfServiceHoursDay={selfServiceHoursWithinPeriod[0]}
                  hasSelfService={hasSelfService}
                  oddTableItem={tableItemIndex % 2 === 1}
                />
              );
            }

            return dates.map((date) => {
              const label = DateHelper.getLabel(lang, date, "d. LLLL");
              const weekday = getNorwegianWeekday(date);

              tableItemIndex += 1;

              return (
                <SpecialOpeningHoursDay
                  key={`${specialHours._key}-${label}`}
                  label={label}
                  normalHoursDay={specialHours.normalHours?.[weekday]}
                  selfServiceHoursDay={specialHours.selfServiceHours?.[weekday]}
                  hasSelfService={hasSelfService}
                  oddTableItem={tableItemIndex % 2 === 1}
                />
              );
            });
          }
        )}
      </Tbody>
    </StyledTable>
  );
};

export default SpecialOpeningHoursMonth;
